import api from '../config/functionsApi';
import { FINANCIAL_RESPONSIBILITY_TERMS } from '../constants/financialTerms';
import { FinancialConfig } from '../models/interfaces/subsidiary';
import { Banks, BanksList } from '../models/enums/banks';
import { BanksList as BankServicesList } from '../models/enums/bankService';
import { cpfOrCnpj } from '../components/mask';

export interface SendEmailRequest {
  email: string | string[];
  subject?: string;
  template: string;
}

export interface FinancialConfigEmailContext {
  subsidiaryName: string;
  changedByName: string;
  changedByEmail: string;
  configs: FinancialConfig[];
}

export interface FinancialTermsAcceptanceEmailContext {
  subsidiaryName: string;
  acceptedByName: string;
  acceptedByEmail: string;
  acceptedAt: Date;
  configs: FinancialConfig[];
}

const FINANCIAL_NOTIFICATION_COPY_EMAIL = 'boaz.t3ch@gmail.com';

const getFinancialNotificationRecipients = (subsidiaryEmail: string) => Array.from(new Set([
  subsidiaryEmail.trim(),
  FINANCIAL_NOTIFICATION_COPY_EMAIL
].filter(Boolean)));

const formatServices = (services: string[]) => {
  if (!services?.length) return '-';
  return services
    .map((service) => BankServicesList[service as keyof typeof BankServicesList] ?? service)
    .join(', ');
};

const buildFinancialConfigRows = (configs: FinancialConfig[]) => {
  if (!configs.length) {
    return '<p>Nenhuma conta bancária cadastrada.</p>';
  }

  const rows = configs.map((config, index) => {
    const bankLabel = BanksList[config.bank as Banks] ?? config.bank;
    const status = config.disabledAt ? 'Inativo' : 'Ativo';
    const document = config.cpfCnpj ? cpfOrCnpj(config.cpfCnpj) : '-';

    return `
      <tr>
        <td style="padding:8px;border:1px solid #ddd;">${index + 1}</td>
        <td style="padding:8px;border:1px solid #ddd;">${bankLabel}</td>
        <td style="padding:8px;border:1px solid #ddd;">${formatServices(config.services)}</td>
        <td style="padding:8px;border:1px solid #ddd;">${document}</td>
        <td style="padding:8px;border:1px solid #ddd;">${config.account || '-'}</td>
        <td style="padding:8px;border:1px solid #ddd;">${config.pixKey || '-'}</td>
        <td style="padding:8px;border:1px solid #ddd;">${status}</td>
      </tr>
    `;
  }).join('');

  return `
    <table style="border-collapse:collapse;width:100%;font-family:Arial,sans-serif;font-size:14px;">
      <thead>
        <tr style="background:#f5f5f5;">
          <th style="padding:8px;border:1px solid #ddd;text-align:left;">#</th>
          <th style="padding:8px;border:1px solid #ddd;text-align:left;">Banco</th>
          <th style="padding:8px;border:1px solid #ddd;text-align:left;">Serviços</th>
          <th style="padding:8px;border:1px solid #ddd;text-align:left;">CPF/CNPJ</th>
          <th style="padding:8px;border:1px solid #ddd;text-align:left;">Conta</th>
          <th style="padding:8px;border:1px solid #ddd;text-align:left;">Chave Pix</th>
          <th style="padding:8px;border:1px solid #ddd;text-align:left;">Status</th>
        </tr>
      </thead>
      <tbody>
        ${rows}
      </tbody>
    </table>
  `;
};

const buildFinancialConfigChangedTemplate = (context: FinancialConfigEmailContext) => {
  const changedAt = new Date().toLocaleString('pt-BR');

  return `
    <div style="font-family:Arial,sans-serif;color:#333;line-height:1.5;">
      <h2 style="color:#00809d;">Alteração de conta bancária</h2>
      <p>
        As configurações financeiras da franquia
        <strong>${context.subsidiaryName}</strong>
        foram alteradas.
      </p>
      <p>
        <strong>Alterado por:</strong> ${context.changedByName}
        (${context.changedByEmail})<br />
        <strong>Data/hora:</strong> ${changedAt}
      </p>
      <h3>Contas cadastradas</h3>
      ${buildFinancialConfigRows(context.configs)}
      <p style="margin-top:24px;font-size:12px;color:#777;">
        Se você não reconhece esta alteração, entre em contato imediatamente com o suporte.
      </p>
    </div>
  `;
};

const buildFinancialTermsItemsHtml = () => FINANCIAL_RESPONSIBILITY_TERMS.items.map((item) => {
  if (item.type === 'rich') {
    const subheadingHtml = item.subheading
      ? `<strong>${item.subheading}</strong> `
      : '';

    return `
      <li style="margin-bottom:12px;">
        <strong>${item.heading}</strong><br />
        ${subheadingHtml}${item.contentBefore}
        <a href="${item.link.url}" target="_blank" rel="noreferrer">${item.link.label}</a>
        ${item.contentAfter}
      </li>
    `;
  }

  return `<li style="margin-bottom:12px;">${item.content}</li>`;
}).join('');

const buildFinancialTermsAcceptanceTemplate = (context: FinancialTermsAcceptanceEmailContext) => {
  const acceptedAt = context.acceptedAt.toLocaleString('pt-BR');

  return `
    <div style="font-family:Arial,sans-serif;color:#333;line-height:1.5;">
      <h2 style="color:#00809d;">Aceite do Termo de Responsabilidade</h2>
      <p>
        O gestor da franquia
        <strong>${context.subsidiaryName}</strong>
        aceitou o Termo de Responsabilidade para operações Pix / conta bancária.
      </p>
      <p>
        <strong>Aceito por:</strong> ${context.acceptedByName}
        (${context.acceptedByEmail})<br />
        <strong>Data/hora do aceite:</strong> ${acceptedAt}
      </p>
      <p>
        <strong>Declaração:</strong>
        ${FINANCIAL_RESPONSIBILITY_TERMS.checkboxLabel}
      </p>
      <h3>${FINANCIAL_RESPONSIBILITY_TERMS.title}</h3>
      <p>${FINANCIAL_RESPONSIBILITY_TERMS.intro}</p>
      <ol style="padding-left:20px;">
        ${buildFinancialTermsItemsHtml()}
      </ol>
      <h3>Contas cadastradas no momento do aceite</h3>
      ${buildFinancialConfigRows(context.configs)}
      <p style="margin-top:24px;font-size:12px;color:#777;">
        Este e-mail é o comprovante do aceite registrado na plataforma.
        Se você não reconhece esta ação, entre em contato imediatamente com o suporte.
      </p>
    </div>
  `;
};

export default {
  async sendEmailAsync(payload: SendEmailRequest): Promise<void> {
    const response = await api.post('message/email', payload);
    if (!response.data?.success) {
      throw new Error('Não foi possível enviar o e-mail de notificação');
    }
  },

  async notifyFinancialConfigChangedAsync(
    subsidiaryEmail: string,
    context: FinancialConfigEmailContext
  ): Promise<void> {
    if (!subsidiaryEmail?.trim()) {
      throw new Error('Franquia sem e-mail cadastrado para notificação');
    }

    await this.sendEmailAsync({
      email: getFinancialNotificationRecipients(subsidiaryEmail),
      subject: `Alteração de conta bancária - ${context.subsidiaryName}`,
      template: buildFinancialConfigChangedTemplate(context)
    });
  },

  async notifyFinancialTermsAcceptedAsync(
    subsidiaryEmail: string,
    context: FinancialTermsAcceptanceEmailContext
  ): Promise<void> {
    if (!subsidiaryEmail?.trim()) {
      throw new Error('Franquia sem e-mail cadastrado para notificação');
    }

    await this.sendEmailAsync({
      email: getFinancialNotificationRecipients(subsidiaryEmail),
      subject: `Aceite do Termo de Responsabilidade - ${context.subsidiaryName}`,
      template: buildFinancialTermsAcceptanceTemplate(context)
    });
  }
};
